/**
 * Typed HTTP client.
 *
 * Transport over the same DTOs the server returns; there is no second set of
 * pricing types here. Nothing happens on import: no request, no base URL from
 * the environment, no global fetch captured at module load.
 */

import type {
  ApiEnvelope,
  ChangeQuery,
  EstimateItem,
  HistoryQuery,
  HttpResponses,
  ObservationContext,
  OfferingQuery,
  PriceListQuery,
} from "../contracts/index.js";
import { PricingError, type PricingErrorCode } from "../contracts/errors.js";

export interface PricingClientOptions {
  readonly baseUrl: string;
  /** Injected so the client works in Node, a browser and a test alike. */
  readonly fetch?: typeof globalThis.fetch;
  readonly token?: string;
  readonly defaultHeaders?: Readonly<Record<string, string>>;
}

/** Kept under its original name for callers that imported it from here. */
export type Envelope<T> = ApiEnvelope<T>;

export interface RequestOptions {
  readonly signal?: AbortSignal;
}

function toQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query === "" ? "" : `?${query}`;
}

export class PricingClient {
  readonly #baseUrl: string;
  readonly #fetch: typeof globalThis.fetch;
  readonly #token: string | undefined;
  readonly #headers: Readonly<Record<string, string>>;

  constructor(options: PricingClientOptions) {
    this.#baseUrl = options.baseUrl.replace(/\/+$/, "");
    // Bound to its global: an unbound window.fetch throws "Illegal invocation"
    // when called as a method of this object.
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.#token = options.token;
    this.#headers = options.defaultHeaders ?? {};
  }

  async #request<T>(
    path: string,
    init: RequestInit & RequestOptions = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      accept: "application/json",
      ...this.#headers,
      ...((init.headers as Record<string, string>) ?? {}),
    };
    if (this.#token) headers.authorization = `Bearer ${this.#token}`;
    const response = await this.#fetch(`${this.#baseUrl}${path}`, { ...init, headers });
    const text = await response.text();
    const payload = text === "" ? null : (JSON.parse(text) as unknown);

    if (!response.ok) {
      const error = (payload as { error?: { code?: string; message?: string; details?: [] } })?.error;
      throw new PricingError(
        (error?.code as PricingErrorCode) ?? "INTERNAL_ERROR",
        error?.message ?? `Request to ${path} failed with ${response.status}.`,
        error?.details ?? [],
      );
    }
    return payload as T;
  }

  providers(query: { q?: string } = {}, options: RequestOptions = {}) {
    return this.#request<HttpResponses["listProviders"]>(`/api/providers${toQuery(query)}`, options);
  }

  models(query: { q?: string; publisher_id?: string } = {}, options: RequestOptions = {}) {
    return this.#request<HttpResponses["listModels"]>(`/api/models${toQuery(query)}`, options);
  }

  offerings(query: OfferingQuery = {}, options: RequestOptions = {}) {
    return this.#request<HttpResponses["listOfferings"]>(
      `/api/offerings${toQuery(query as Record<string, unknown>)}`,
      options,
    );
  }

  prices(query: PriceListQuery = {}, options: RequestOptions = {}) {
    return this.#request<HttpResponses["listPrices"]>(
      `/api/prices${toQuery(query as Record<string, unknown>)}`,
      options,
    );
  }

  priceWindows(query: HistoryQuery, options: RequestOptions = {}) {
    return this.#request<HttpResponses["listPriceWindows"]>(
      `/api/price-windows${toQuery(query as unknown as Record<string, unknown>)}`,
      options,
    );
  }

  priceChanges(query: ChangeQuery = {}, options: RequestOptions = {}) {
    return this.#request<HttpResponses["listPriceChanges"]>(
      `/api/price-changes${toQuery(query as Record<string, unknown>)}`,
      options,
    );
  }

  statistics(query: ChangeQuery = {}, options: RequestOptions = {}) {
    return this.#request<HttpResponses["getPricingStatistics"]>(
      `/api/pricing-statistics${toQuery(query as Record<string, unknown>)}`,
      options,
    );
  }

  sources(query: { source_id?: string; enabled?: boolean } = {}, options: RequestOptions = {}) {
    return this.#request<HttpResponses["listSources"]>(`/api/sources${toQuery(query)}`, options);
  }

  capture(
    captureId: string,
    query: { excerpt_offset?: number; excerpt_limit?: number; excerpt_path?: string } = {},
    options: RequestOptions = {},
  ) {
    return this.#request<HttpResponses["getCapture"]>(
      `/api/captures/${encodeURIComponent(captureId)}${toQuery(query)}`,
      options,
    );
  }

  crawlRuns(query: { source_id?: string; status?: string } = {}, options: RequestOptions = {}) {
    return this.#request<HttpResponses["listCrawlRuns"]>(`/api/crawl-runs${toQuery(query)}`, options);
  }

  crawlRun(runId: string, options: RequestOptions = {}) {
    return this.#request<HttpResponses["getCrawlRun"]>(`/api/crawl-runs/${encodeURIComponent(runId)}`, options);
  }

  /** Queue a refresh. The idempotency key is required by the server. */
  startCrawlRun(
    sourceId: string,
    idempotencyKey: string,
    options: RequestOptions = {},
  ) {
    return this.#request<HttpResponses["startCrawlRun"]>("/api/crawl-runs", {
      ...options,
      method: "POST",
      headers: { "content-type": "application/json", "idempotency-key": idempotencyKey },
      body: JSON.stringify({ source_id: sourceId }),
    });
  }

  estimates(
    body: {
      items: readonly EstimateItem[];
      catalog_revision_id?: string;
      at_utc?: string;
      freshness?: string;
      backfill?: string;
      source_id?: string;
    },
    options: RequestOptions = {},
  ) {
    return this.#request<HttpResponses["estimate"]>("/api/estimates", {
      ...options,
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  catalogRevisions(query: { from_utc?: string; to_utc?: string } = {}, options: RequestOptions = {}) {
    return this.#request<HttpResponses["listCatalogRevisions"]>(
      `/api/catalog-revisions${toQuery(query)}`,
      options,
    );
  }

  catalogRevision(revisionId: string, options: RequestOptions = {}) {
    return this.#request<HttpResponses["getCatalogRevision"]>(
      `/api/catalog-revisions/${encodeURIComponent(revisionId)}`,
      options,
    );
  }

  /** Download a snapshot. The caller validates it before trusting it. */
  snapshot(
    revisionId: string,
    query: { history?: "full" | "current"; at_utc?: string } = {},
    options: RequestOptions = {},
  ) {
    return this.#request<HttpResponses["getCatalogSnapshot"]>(
      `/api/catalog-revisions/${encodeURIComponent(revisionId)}/snapshot${toQuery(query)}`,
      options,
    );
  }
}

export function createPricingClient(options: PricingClientOptions): PricingClient {
  return new PricingClient(options);
}

export type { ObservationContext };
